import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { hash } from 'rsvp';
import { UserRoles } from 'uplisting-frontend/models/user';
import PermittedRoute from 'uplisting-frontend/pods/base/permitted-route';
import ActionChangeoversSummaryController from 'uplisting-frontend/pods/action/changeovers/summary/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { ensureTaskPropertyLoaded } from 'uplisting-frontend/utils/task-property';

interface ITaskHost {
  handleTaskSelect(task: ModelRegistry['task']): void;
}

export default abstract class SidebarChangeoversSummaryRoute extends PermittedRoute {
  @service featureFlags!: Services['feature-flags'];

  @service notifications!: Services['notifications'];

  @service('repositories/task')
  taskRepository!: Services['repositories/task'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  permission = 'calendar.changeovers.summary';

  abstract parentRoute: string;

  taskSection?: string;

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);

    if (
      transition.isAborted ||
      !this.taskSection ||
      !this.featureFlags.gate.enabled('task_centre')
    ) {
      return;
    }

    await this.handOffToTaskPanel(this.taskSection);
  }

  model() {
    const changeover = this.modelFor(
      this.parentRoute,
    ) as ModelRegistry['changeover'];

    if (
      changeover.property.changeoverSetting?.automatic &&
      changeover.teammate
    ) {
      return hash({ changeover, teammates: [] });
    }

    return hash({
      changeover,
      teammates: this.store.query('teammate', {
        property_id: changeover.property.id,
        role: UserRoles.housekeeper,
      }),
    });
  }

  setupController(
    controller: ActionChangeoversSummaryController,
    model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    Object.assign(controller, model);
  }

  private async handOffToTaskPanel(section: string): Promise<void> {
    const changeover = this.modelFor(
      this.parentRoute,
    ) as ModelRegistry['changeover'];

    if (!changeover.taskId) {
      return;
    }

    const task = await this.taskForChangeover(changeover.taskId);

    if (!task) {
      this.notifications.error(
        changeover.teammate
          ? 'action.tasks.assigned_elsewhere'
          : 'action.tasks.unavailable',
      );
      this.router.replaceWith(section);

      return;
    }

    await ensureTaskPropertyLoaded(task, this.store, this.propertyRepository);

    (this.controllerFor(section) as unknown as ITaskHost).handleTaskSelect(
      task,
    );

    this.router.replaceWith(section);
  }

  private async taskForChangeover(
    taskId: string,
  ): Promise<ModelRegistry['task'] | undefined> {
    try {
      return await this.taskRepository.findRecord(taskId, { reload: true });
    } catch {
      return undefined;
    }
  }
}
